/**
 * The fetch boundary.
 *
 * Every HTTP call this app makes goes through `apiRequest`, which does two
 * things no call site is allowed to do for itself: it builds the request, and it
 * `.parse()`s the reply with the SAME `@swarm/shared` schema the API validated
 * its response against. A deployed API that drifts from the client therefore
 * surfaces here, naming the offending field, instead of leaking `undefined` into
 * a component three renders later.
 *
 * Failure contract, transcribed from the Next.js call sites so the UI behaves
 * identically: a non-2xx reply throws an `ApiError` whose `message` is the
 * server's `error` string. Several endpoints return a fully zero-filled payload
 * ALONGSIDE that error, so the parsed body is carried on `ApiError.body` for the
 * two screens that render it.
 */
import { logger } from "@swarm/shared/logger";
import type { z } from "zod";

/** Error envelope every route group falls back to (`onError` in apps/api). */
type ErrorBody = { error?: string };

/**
 * A non-2xx reply, or a reply whose body did not match its schema.
 *
 * `body` is the parsed JSON when there was one — used by the draw-eligibility
 * screen, which renders the zero-filled payload that accompanies the error.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** Options accepted by `apiRequest`. `body` is serialized here, never by callers. */
export type ApiRequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** Serialized as JSON with the matching `Content-Type`. */
  body?: unknown;
  signal?: AbortSignal;
};

/**
 * Issues a request and returns the schema-parsed body.
 *
 * Throws `ApiError` on a transport failure, a non-2xx status, or a body that
 * fails `schema`. Never returns a value the schema did not accept — that is the
 * whole point of routing every call through here.
 */
export const apiRequest = async <TSchema extends z.ZodType>(
  url: string,
  schema: TSchema,
  options: ApiRequestOptions = {},
): Promise<z.infer<TSchema>> => {
  const { method = "GET", body, signal } = options;

  let response: Response;

  try {
    response = await fetch(url, {
      method,
      signal,
      // Set unconditionally: the API answers JSON for every route, and a GET
      // with the header costs nothing.
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    // Network-level failure: there is no status and no body to report.
    throw new ApiError(
      cause instanceof Error ? cause.message : "Network request failed",
      0,
      undefined,
    );
  }

  const raw: unknown = await response.json().catch(() => undefined);

  if (!response.ok) {
    const message = (raw as ErrorBody | undefined)?.error;

    // Parsed leniently: on an error path the body may be `{ error }` rather than
    // the success shape, so it is attached as-is instead of being validated.
    throw new ApiError(message ?? `HTTP error! status: ${response.status}`, response.status, raw);
  }

  const parsed = schema.safeParse(raw);

  if (!parsed.success) {
    logger.error(
      { url, status: response.status, issues: parsed.error.issues },
      "API response failed its shared schema",
    );

    throw new ApiError(`Unexpected response shape from ${url}`, response.status, raw);
  }

  return parsed.data;
};

/**
 * The message a toast should show for a thrown value.
 *
 * Centralized because the ported call sites all wrote the same
 * `error instanceof Error ? error.message : "<fallback>"` ternary.
 */
export const apiErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message.length > 0 ? error.message : fallback;
